<template>
  <div
    class="idPhoto"
  >
    <!-- ID modal -->
    <IdModal
      :show="showModal"
      header-class="id-modal"
      :body-class="isOnboardingRedesign ? `id-body-modal tw-max-w-[750px] tw-mx-auto` : `id-body-modal`"
      :full-width="isOnboardingRedesign ? true : false"
      @exit="hideModal()"
    >
      <template #header>
        <img
          :src="isOnboardingRedesign ? require('@/assets/images/id.svg') : require('@/assets/images/id.png')"
          alt=""
          class="tw-h-[150px] lg:tw-h-[200px]"
        >
      </template>
      <template #body>
        <h3
          :class="{'tw-mt-10 tw-text-[#787878] tw-capitalize tw-text-xl md:tw-text-lg': isOnboardingRedesign, 'sub-text text-uppercase text-primary tw-mt-10 tw-tracking-[0.16em]': !isOnboardingRedesign}"
        >
          Identity Verification
        </h3>

        <h2 :class="isOnboardingRedesign && 'tw-text-2xl md:tw-text-[26px]'">
          Why do you need a photo of my ID?
        </h2>
        <p
          class="sub-text"
          :class="{'tw-text-lg': isOnboardingRedesign}"
        >
          We use your ID photo to verify your identity, for example your name
          and date of birth. Telemedicine laws require us to verify this
          information before prescribing medication.
        </p>

        <h2
          class="tw-text-2xl"
          :class="isOnboardingRedesign && 'tw-text-2xl md:tw-text-[26px]'"
        >
          Who sees this?
        </h2>
        <p
          class="sub-text"
          :class="{ 'tw-text-lg tw-pb-44': isOnboardingRedesign }"
        >
          The photo of your ID will be stored securely and will only be shared
          with the patient support team and our identity verification platform.
        </p>
        <div :class="isOnboardingRedesign ? 'tw-text-center tw-fixed tw-bg-white tw-bottom-0 tw-left-0 tw-right-0 tw-px-4' : 'tw-text-left'">
          <b-button
            pill
            block
            variant="info"
            class="slight-rounded"
            :class="isOnboardingRedesign ? 'tw-max-w-[300px] tw-mx-auto tw-my-4' : ' tw-mb-10 mt-5'"
            @click="hideModal()"
          >
            I understand
          </b-button>
        </div>
      </template>
    </IdModal>
    <div
      class="bg-overlay"
      @click="hideModal()"
    ></div>

    <h2 class="question-title with-subtext">
      {{ isAndroid11Webview ? "Send us" : "Upload" }} a photo of your ID.
    </h2>
    <p class="sub-text mb-4">
      In order to legally prescribe medications, we need a valid
      government-issued ID that matches your name and date of birth.
      <a
        style="color: #828282"
        :class="{'tw-block tw-mt-6 tw-text-[#1A6A72] tw-text-lg': isOnboardingRedesign}"
        class="text-underline"
        @click="openModal()"
      >
        Why do you need this?
      </a>
    </p>
    <template v-if="isOnboardingRedesign">
      <p class="tw-text-2xl tw-mb-4">
        Acceptable Forms of ID:
      </p>
      <b-row class="tw-mb-6">
        <b-col
          cols="12"
          class="list-id"
        >
          <ul>
            <li class="tw-text-lg">
              - Driver's License
            </li>
            <li class="tw-text-lg">
              - Passport
            </li>
            <li class="tw-text-lg">
              - Identification Card
            </li>
            <li class="tw-text-lg">
              - Permanent Resident Card
            </li>
            <li class="tw-text-lg">
              - Consular Card
            </li>
          </ul>
        </b-col>
      </b-row>
    </template>
    <template v-else>
      <p class="sub-text mb-0">
        Acceptable Forms of ID
      </p>
      <b-row class="mb-3">
        <b-col
          cols="12"
          md="6"
          class="list-id"
        >
          <ul>
            <li>Driver License</li>
            <li>Passport</li>
            <li>Identification Card</li>
          </ul>
        </b-col>
        <b-col
          cols="12"
          md="6"
        >
          <ul>
            <li>Permanent Resident Card</li>
            <li>Consular Card</li>
          </ul>
        </b-col>
      </b-row>
    </template>
    <div
      v-if="!isMobileOrTablet"
      class="fs-exclude"
    >
      <div
        class="photo-holder fs-mask"
        :class="{'tw-rounded-xl': isOnboardingRedesign}"
      >
        <div
          v-if="(picture || idPreview) && uploadFileType !== 'application/pdf'"
          class="btn-crop"
          @click="openCropModal"
        >
          <b-icon icon="crop" />
        </div>
        <div
          class="photo"
          :class="{'tw-scale-125': isOnboardingRedesign}"
          :style="{
            backgroundImage:
              isPhotoTaken || showId || identificationURL
                ? ''
                : 'url(' + (isOnboardingRedesign ? require('@/assets/images/id.svg') : require('@/assets/images/id.png')) + ')',
          }"
        >
          <img
            v-if="isPhotoTaken"
            :src="picture"
            class="fs-exclude"
            alt=""
          >
          <img
            v-else-if="showId"
            :src="idPreview"
            class="fs-exclude"
            style="object-fit: contain"
          >
          <img
            v-else-if="identificationURL"
            class="fs-exclude"
            :style="{
              width:
                identificationFileType === 'application/pdf' ? 'auto' : '100%',
            }"
            :src="identificationURL"
          >
        </div>
      </div>

      <div
        v-if="!isOnboardingRedesign"
        class="tw-flex tw-flex-col tw-gap-2 tw-mt-2"
      >
        <div v-if="identificationUploadStatus !== 'UPLOADED' && !isAndroid11Webview">
          <div class="text-center">
            <PersonalPhotoTroubleUpload @skip="submitAnswer({ isSkipped: true })" />
          </div>
        </div>

        <div class="tw-text-center">
          <OnboardingTLSSecurity />
        </div>
      </div>

      <OnboardingCTAContainer custom>
        <div
          class="tw-flex tw-gap-2 tw-w-full"
          :class="{
            'tw-justify-center tw-flex-col md:tw-flex-row tw-mx-auto': isOnboardingRedesign,
            'tw-flex-col': !isOnboardingRedesign,
            'lg:!tw-max-w-[1000px]': isOnboardingRedesign && (isPhotoTaken || showId || identificationURL),
            'lg:tw-max-w-[600px]': isOnboardingRedesign && !(isPhotoTaken || showId || identificationURL),
          }"
        >
          <template v-if="isOnboardingRedesign">
            <template v-if="!isPhotoTaken && !showId && !identificationURL">
              <b-button
                block
                pill
                :variant="isOnboardingRedesign ? 'outline-info' : 'info'"
                class="photo-btn slight-rounded"
                :class="{'tw-border-[#312849] tw-text-[#312849]': isOnboardingRedesign}"
                :disabled="isLoadingImage"
              >
                <input
                  id="identification"
                  ref="identification"
                  type="file"
                  :accept="ACCEPTED_FILE_MIME_TYPES"
                  class="hide-file-input"
                  @change="idUpload()"
                >
                <label
                  for="identification"
                  class="mb-0"
                >
                  {{ isLoadingImage ? "Loading..." : "SELECT PHOTO" }}
                </label>
              </b-button>

              <b-button
                :variant="isOnboardingRedesign ? 'info' : 'outline-info'"
                :disabled="saving"
                pill
                block
                class="tw-m-0 slight-rounded"
                @click="showCamera"
              >
                {{ isPhotoTaken ? "RETAKE" : "TAKE PHOTO" }}
              </b-button>
            </template>
            <template v-else>
              <b-button
                pill
                block
                variant="outline-info"
                :disabled="saving || isLoadingImage"
                class="photo-btn tw-m-0 slight-rounded"
                :class="{'tw-border-[#312849] tw-text-[#312849]': isOnboardingRedesign}"
              >
                <input
                  id="identification"
                  ref="identification"
                  type="file"
                  :accept="ACCEPTED_FILE_MIME_TYPES"
                  class="hide-file-input"
                  @change="idUpload()"
                >
                <label
                  for="identification"
                  class="mb-0"
                >
                  {{ isLoadingImage ? "Loading..." : "REUPLOAD" }}
                </label>
              </b-button>
  
              <b-button
                variant="outline-info"
                :disabled="saving || isLoadingImage"
                pill
                block
                class="tw-m-0 slight-rounded"
                :class="{'tw-border-[#312849] tw-text-[#312849]': isOnboardingRedesign}"
                @click="showCamera"
              >
                {{ isPhotoTaken ? "Retake Photo" : "TAKE PHOTO" }}
              </b-button>
            </template>
          </template>
          <template v-else>
            <div
              v-if="!isPhotoTaken && !showId && !identificationURL"
              class="tw-space-y-2 md:tw-space-y-0 md:tw-flex tw-items-center tw-gap-2"
              :class="{'tw-w-full': isOnboardingRedesign}"
            >
              <b-button
                block
                pill
                :variant="isOnboardingRedesign ? 'outline-info' : 'info'"
                class="photo-btn slight-rounded"
                :class="{'tw-border-[#312849] tw-text-[#312849]': isOnboardingRedesign}"
                :disabled="isLoadingImage"
              >
                <input
                  id="identification"
                  ref="identification"
                  type="file"
                  :accept="ACCEPTED_FILE_MIME_TYPES"
                  class="hide-file-input"
                  @change="idUpload()"
                >
                <label
                  for="identification"
                  class="mb-0"
                >
                  {{ isLoadingImage ? "Loading..." : "SELECT PHOTO" }}
                </label>
              </b-button>
  
              <b-button
                :variant="isOnboardingRedesign ? 'info' : 'outline-info'"
                :disabled="saving"
                pill
                block
                class="tw-m-0 slight-rounded"
                @click="showCamera"
              >
                {{ isPhotoTaken ? "RETAKE" : "TAKE PHOTO" }}
              </b-button>
            </div>
  
            <div
              v-else
              class="tw-flex md:tw-space-y-0 md:tw-flex tw-items-center tw-gap-2"
              :class="{'tw-flex tw-flex-col tw-w-full md:tw-flex-row': isOnboardingRedesign}"
            >
              <b-button
                pill
                block
                variant="outline-info"
                :disabled="saving || isLoadingImage"
                class="photo-btn tw-m-0 slight-rounded"
                :class="{'tw-border-[#312849] tw-text-[#312849]': isOnboardingRedesign}"
              >
                <input
                  id="identification"
                  ref="identification"
                  type="file"
                  :accept="ACCEPTED_FILE_MIME_TYPES"
                  class="hide-file-input"
                  @change="idUpload()"
                >
                <label
                  for="identification"
                  class="mb-0"
                >
                  {{ isLoadingImage ? "Loading..." : "REUPLOAD" }}
                </label>
              </b-button>
  
              <b-button
                variant="outline-info"
                :disabled="saving || isLoadingImage"
                pill
                block
                class="tw-m-0 slight-rounded"
                :class="{'tw-border-[#312849] tw-text-[#312849]': isOnboardingRedesign}"
                @click="showCamera"
              >
                {{ isPhotoTaken ? "Retake Photo" : "TAKE PHOTO" }}
              </b-button>
            </div>
          </template>

          <template v-if="isPhotoTaken || showId || identificationURL">
            <b-button
              class="tw-m-0 slight-rounded"
              pill
              variant="info"
              :disabled="saving"
              block
              @click="submitAnswer({ isSkipped: false })"
            >
              {{ saving ? "Saving..." : "CONFIRM" }}
            </b-button>
          </template>
        </div>
      </OnboardingCTAContainer>

      <template v-if="isOnboardingRedesign">
        <div class="tw-flex tw-flex-col tw-gap-2 tw-mt-8">
          <OnboardingCertifiedSeal
            v-if="step === 'idphoto'"
            class="tw-flex tw-justify-center tw-mb-4"
          />
          <div v-if="identificationUploadStatus !== 'UPLOADED' && !isAndroid11Webview">
            <div class="text-center">
              <PersonalPhotoTroubleUpload @skip="$emit('setStep', 'idphotoskip')" />
            </div>
          </div>
  
          <div class="tw-text-center tw-text-[#848484] tw-mb-4">
            <OnboardingTLSSecurity />
          </div>
          
          <IDPhotoEnsure page="id" />
        </div>
      </template>
    </div>
    <div v-else>
      <template v-if="isOnboardingRedesign">
        <MobileUpload
          v-if="step === 'idphoto'"
          page="id"
          class="fs-exclude"
          id-size
          :is-android11-webview.sync="isAndroid11Webview"
          :existing-photo="identificationURL"
          :existing-photo-file-type="identificationFileType"
          :step="step"
          @submitAnswer="setMobilePhoto"
          @showAlert="showAlert"
          @hideAlert="hideAlert"
          @skip="$emit('setStep', 'idphotoskip')"
        />
      </template>
      <template v-else>
        <MobileUpload
          page="id"
          class="fs-exclude"
          id-size
          :is-android11-webview.sync="isAndroid11Webview"
          :existing-photo="identificationURL"
          :existing-photo-file-type="identificationFileType"
          @submitAnswer="setMobilePhoto"
          @showAlert="showAlert"
          @hideAlert="hideAlert"
          @skip="submitAnswer({ isSkipped: true })"
        />
      </template>
    </div>

    <IDPhotoEnsure v-if="!isOnboardingRedesign" />

    <template v-if="isOnboardingRedesign">
      <div
        v-if="isErrorShown"
        ref="idError"
      >
        <ErrorLabel>
          The file size or type is incorrect. Please upload an accepted file type (png, jpg, jpeg, pdf, heic, tiff, pjpeg) that doesn’t exceed 25 MB.
        </ErrorLabel>
      </div>
    </template>

    <modal
      id="takePhoto"
      size="md"
      centered
      hide-header
      hide-footer
      no-close-on-esc
      no-close-on-backdrop
    >
      <video
        ref="camera"
        :class="{'tw-rounded-xl': isOnboardingRedesign}"
        width="100%"
        height="325"
        autoplay
        style="background-color: #000"
      ></video>
      <canvas
        ref="canvas"
        class="photo-taken"
        :width="325"
        :height="250"
      ></canvas>
      <b-row
        :class="{'tw-flex tw-flex-col-reverse md:tw-flex-row-reverse lg:tw-flex-col-reverse tw-gap-2 md:tw-max-w-full lg:tw-max-w-[300px] tw-mx-auto !tw-mt-10': isOnboardingRedesign, 'mt-3': !isOnboardingRedesign}"
      >
        <b-col
          cols="12"
          :md="isOnboardingRedesign ? '' : '6'"
          :class="{'tw-px-0': isOnboardingRedesign}"
        >
          <b-button
            pill
            block
            variant="info"
            class="slight-rounded"
            @click="takePhoto"
          >
            Capture
          </b-button>
          <div
            v-if="!isOnboardingRedesign"
            class="mb-3 d-md-none"
          ></div>
        </b-col>

        <b-col
          cols="12"
          :md="isOnboardingRedesign ? '' : '6'"
          :class="{'tw-px-0': isOnboardingRedesign}"
        >
          <b-button
            pill
            block
            variant="outline-info"
            class="slight-rounded"
            :class="{'tw-border-[#312849] tw-text-[#312849]': isOnboardingRedesign}"
            @click="hideCameraModal"
          >
            Cancel
          </b-button>
        </b-col>
      </b-row>
    </modal>

    <b-modal
      id="crop"
      class="fs-exclude"
      size="md"
      centered
      hide-header
      hide-footer
      no-close-on-esc
      no-close-on-backdrop
    >
      <vue-croppie
        ref="croppieRef"
        :enable-orientation="false"
        :enable-resize="false"
        :boundary="{ width: 300, height: 200 }"
        :viewport="{ width: 300, height: 200 }"
      />
      <b-row class="mt-3">
        <b-col
          cols="12"
          md="6"
        >
          <b-button
            pill
            block
            variant="info"
            class="slight-rounded"
            @click="cropImage"
          >
            Crop
          </b-button>
          <div class="mb-3 d-md-none"></div>
        </b-col>

        <b-col
          cols="12"
          md="6"
        >
          <b-button
            pill
            block
            variant="outline-info"
            class="slight-rounded"
            @click="closeCropModal"
          >
            Cancel
          </b-button>
        </b-col>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex';
// services
import { isMobile } from 'mobile-device-detect';
import { OnboardingService } from '@/services/onboarding.service';

// components
import MobileUpload from '@/components/partials/MobileUpload';
import IdModal from '@/components/partials/SideModal';
import OnboardingCTAContainer from '@/components/onboarding/OnboardingCTAContainer';
import IDPhotoEnsure from '@/components/onboarding/verification/IDPhotoEnsure';
import OnboardingTLSSecurity from '@/components/onboarding/OnboardingTLSSecurity'
import PersonalPhotoTroubleUpload from '@/components/onboarding/verification/PersonalPhotoTroubleUpload'
import OnboardingCertifiedSeal from '@/components/onboarding/OnboardingCertifiedSeal';

// libs
import pdfIcon from '@/assets/images/onboarding/pdf-icon.png';

// constants
import { ACCEPTED_FILE_MIME_TYPES } from '@/constants/files';

// utils
import {
  finalizeFileForUpload,
  fileToDataUrl,
  isFileTypePdf,
} from '@/utils/file';
import { getRef } from '@corefront/utils/getRef';

// mixins
import analyticsMixin from '@/mixins/analytics';
import ErrorLabel from '@/components/partials/ErrorLabel.vue';
import CreditModal from '@/components/partials/SideModal.vue';

export default {
  components: {
    CreditModal,
    ErrorLabel,
    MobileUpload,
    IdModal,
    OnboardingCTAContainer,
    OnboardingTLSSecurity,
    PersonalPhotoTroubleUpload,
    OnboardingCertifiedSeal,
    IDPhotoEnsure
  },
  mixins: [analyticsMixin],
  props: {
    onboardingflow: {
      type: String,
      default: 'flowA',
    },
    analyticsId: {
      type: String,
      default: '',
    },
    state: {
      type: String,
      default: '',
    },
    step: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      ACCEPTED_FILE_MIME_TYPES,
      showModal: false,
      userid: null,
      currentonboardingstep: null,
      isPhotoTaken: false,
      showId: false,
      isLoadingImage: false,
      identificationURL: null,
      saving: false,
      idPreview: '',
      picture: '',
      orientation: 0,

      email: '',
      firstname: '',
      lastname: '',
      phoneno: '',

      identification: null,
      identificationFileType: '',
      identificationUploadStatus: '',

      isAndroid11Webview: false,
      isErrorShown: false
    };
  },
  computed: {
    ...mapState('global', ['isOnboardingRedesign']),
    isMobileOrTablet() {
      return isMobile;
    },
    uploadFileType() {
      return this.identification?.type || '';
    },
  },
  watch: {
    saving: {
      handler(val) {
        this.$store.commit('onboarding/SET_IS_IMAGE_UPLOAD_PROCESSING', val);
      },
      immediate: true,
    },
  },
  async mounted () {
    window.scrollTo(0, 0);

    if (!this.isMobileOrTablet) {
      const identification = await getRef.call(this, 'identification')
      identification.value = null
    }

    const data = await this.$store.dispatch('onboarding/getPatientOnboarding');
    this.userid = data.id;
    this.email = data.email;
    this.firstname = data.firstname;
    this.lastname = data.lastname;
    this.phoneno = data.phoneno;

    this.$_analytics_track('Onboarding Screen Viewed', {
      customerId: this.analyticsId,
      screen: '4.2-A',
      firstname: this.firstname,
      lastname: this.lastname,
      onboardingflow: this.onboardingflow,
    })

    this.currentonboardingstep = data.onboardingstep;
    this.identificationUploadStatus = data.identificationupload;
    if (data.identificationURL) {
      this.identificationFileType = data.identificationFileType;
      if (this.identificationFileType === 'application/pdf') {
        this.identificationURL = pdfIcon;
      } else {
        this.identificationURL = data.identificationURL;
      }
    }
  },
  methods: {
    openModal() {
      this.elementTag('hidden', true);
    },
    hideModal() {
      this.elementTag(null, false);
    },
    elementTag(style, Boolean) {
      this.showModal = Boolean;
      const htmlTag = document.documentElement;
      htmlTag.style.overflow = style;
      // htmlTag.scrollTop = htmlTag.scrollHeight;
    },
    openCropModal() {
      this.$root.$emit('bv::show::modal', 'crop');
      this.$nextTick(() => {
        this.$refs.croppieRef.bind({
          url: this.idPreview,
        });
      });
    },
    closeCropModal() {
      this.$root.$emit('bv::hide::modal', 'crop');
    },
    cropImage() {
      const options = {
        format: 'jpeg',
      };
      this.$refs.croppieRef.result(options, output => {
        this.idPreview = output;
        this.picture = output;
        this.identification = this.dataURLtoFile(output, 'image');
        this.closeCropModal();
      });
    },
    setMobilePhoto(photo) {
      this.picture = photo;
      this.idPreview = photo;
      const reader = new FileReader();
      reader.addEventListener(
        'load',
        function () {
          this.isPhotoTaken = false;
          this.showId = true;
          this.idPreview = reader.result;
        }.bind(this),
        false
      );
      this.submitAnswer({ isSkipped: false });
    },
    showAlert(msg) {
      this.$emit('showAlert', msg);
    },
    hideAlert() {
      this.$emit('hideAlert');
    },
    showCamera() {
      this.$root.$emit('bv::show::modal', 'takePhoto');
      this.createCameraElement();
    },
    createCameraElement() {
      const constraints = (window.constraints = {
        audio: false,
        video: {
          facingMode: 'user',
        },
      });
      navigator.mediaDevices
        .getUserMedia(constraints)
        .then(stream => {
          this.$refs.camera.srcObject = stream;
        })
        .catch(error => {
          if (error.name == 'NotAllowedError') {
            this.hideCameraModal();
            this.$emit(
              'showAlert',
              'Camera is not detected. Please turn on your camera and refresh your browser.'
            );
            alert(
              'Camera is not detected. Please turn on your camera and refresh your browser.'
            );
          }
        });
    },
    stopCameraStream() {
      if (this.$refs.camera?.srcObject) {
        const tracks = this.$refs.camera.srcObject.getTracks();
        tracks.forEach(track => {
          track.stop();
        });
      }
    },
    hideCameraModal() {
      this.stopCameraStream();
      this.$root.$emit('bv::hide::modal', 'takePhoto');
    },
    takePhoto() {
      try {
        const canvas = this.$refs.canvas;
        const context = canvas.getContext('2d');
        context.drawImage(this.$refs.camera, 0, 0, 325, 250);
        this.picture = canvas.toDataURL('image/png');
        this.idPreview = canvas.toDataURL('image/png');
      } catch (err) {
        const id = this.userid ? this.userid : 'Could not get customer ID';
        const email = this.email ? this.email : 'Could not get customer email';
        this.$store.dispatch('sendErrorLog', {
          step: 'Identity Verification - ID Take Photo',
          cus_id: id,
          email: email,
          error_log: err,
        });
        this.$emit('showAlert', err.message);
      }

      this.$nextTick(() => {
        this.$root.$emit('bv::hide::modal', 'takePhoto');
        this.stopCameraStream();
        this.isPhotoTaken = true;
      });
    },
    async idUpload() {
      try {
        this.isLoadingImage = true;

        this.hideAlert();

        const fileToUpload = this.$refs.identification.files[0];

        if (!fileToUpload) {
          return;
        }

        const { error, success } = await finalizeFileForUpload(fileToUpload);

        if (error) {
          if (this.isOnboardingRedesign) {
            this.isErrorShown = true
            setTimeout(() => {
              this.$refs.idError?.scrollIntoView({ behavior: 'smooth' });
            }, 1000)
            return
          }
          console.log(error);
          this.showAlert(error);
          return;
        }

        this.identification = success;

        const isPdf = isFileTypePdf(this.identification);
        const dataUrl = await fileToDataUrl(this.identification);

        this.isPhotoTaken = false;
        this.showId = true;
        this.picture = dataUrl;
        this.idPreview = isPdf ? pdfIcon : dataUrl;
        this.isErrorShown = false
      } catch (err) {
        console.log(err);
        const id = this.userid ? this.userid : 'Could not get customer ID';
        const email = this.email ? this.email : 'Could not get customer email';
        this.$store.dispatch('sendErrorLog', {
          step: 'Identity Verification - ID Upload',
          cus_id: id,
          email: email,
          error_log: err,
        });
        this.showAlert(err);
      } finally {
        this.isLoadingImage = false;
      }
    },
    async fbqTrack(data) {
      try {
        // FB Pixel Tracking
        fbq('init', '348219289912097', {
          em: this.email,
          fn: this.firstname,
          ln: this.lastname,
          ph: this.phoneno,
          external_id: this.userid,
          extern_id: this.userid,
        });

        const pageViewBody = { userid: this.userid };
        OnboardingService.onboardingEvent('PageView', pageViewBody);

        fbq(
          'trackCustom',
          'IDUploaded',
          {
            external_id: this.userid,
            extern_id: this.userid,
            state: this.state,
            ...(await this.$store.dispatch('getFBQParams')),
          },
          {
            eventID: data.eventid,
          }
        );
      } catch (err) {
        console.log(err);
      }
    },
    async submitAnswer ({ isSkipped = false }) {
      try {
        this.saving = true;

        if (this.idPreview == '' && this.identificationURL != null) {
          // Segment
          window.analytics.identify(this.analyticsId, {
            iduploaded: this.identificationUploadStatus === 'UPLOADED',
          });

          this.saving = false;
          this.$emit('setStep', 'personalphoto');
          return;
        }

        const mform = new FormData();
        let idFront;

        if (isSkipped) {
          idFront = 'SKIPPED';
        } else {
          if (this.isPhotoTaken === true && this.picture) {
            idFront = this.picture;
          } else if (this.isPhotoTaken === false && this.idPreview) {
            if (this.uploadFileType === 'application/pdf') {
              idFront = this.picture;
            } else {
              idFront = this.idPreview;
            }
          }

          if (idFront) {
            if (!(idFront instanceof File)) {
              idFront = this.dataURLtoFile(idFront, 'image');
            }
          }
        }

        mform.append('identification', idFront);
        if (this.orientation > 0)
          mform.append('rotationindex', this.orientation);

        let res;
        try {
          res = await OnboardingService.updatePatientOnboarding(5.2, mform, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        } catch (err) {
          console.log(err);
          this.$emit('showAlert', err);
          this.saving = false;
          return;
        }
        if (isSkipped) {
          // Segment
          window.analytics.identify(this.analyticsId, {
            iduploaded: false,
          });
          if (this.identificationUploadStatus !== 'SKIPPED') {
            // ID Upload skipped analytics
            await this.$_analytics_track(
              'ID Upload Skipped',
              {
                customerId: this.analyticsId,
                onboaringflow: this.onboardingflow,
                messageID: res.data.eventid,
                phoneno: this.phoneno,
                state: this.state,
                email: this.email,
                firstname: this.firstname,
                lastname: this.lastname,
              },
            );
          }
        } else {
          // Segment
          window.analytics.identify(this.analyticsId, {
            iduploaded: true,
          });
          if (this.identificationUploadStatus !== 'UPLOADED') {
            // SEGMENT
            await this.$_analytics_track(
              'ID Uploaded',
              {
                customerId: this.analyticsId,
                onboardingflow: this.onboardingflow,
                messageID: res.data.eventid,
                phoneno: this.phoneno,
                state: this.state,
                email: this.email,
                firstname: this.firstname,
                lastname: this.lastname,
              },
            );
          }
        }
        if (res.data.eventid) {
          await this.fbqTrack(res.data);
        } else if (
          res.data.eventid &&
          process.env.VUE_APP_SERVER != 'production'
        ) {
          console.log('FB TRACKING IDUploaded');
          console.log('FB TRACKING IDUploaded: ' + res.data.eventid);
        }
        this.$emit('setStep', 'personalphoto');
        this.saving = false;
      } catch (err) {
        if (err.status == 401) {
          this.$router.push('/login');
        } else {
          const id = this.userid ? this.userid : 'Could not get customer ID';
          const email = this.email
            ? this.email
            : 'Could not get customer email';
          this.$store.dispatch('sendErrorLog', {
            step: 'Identity Verification - ID Submission',
            cus_id: id,
            email: email,
            error_log: err,
          });
          this.$emit('showAlert', err);
        }
      }
    },
    dataURLtoFile(dataurl, filename) {
      const arr = dataurl.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n) {
        u8arr[n - 1] = bstr.charCodeAt(n - 1);
        n -= 1; // to make eslint happy
      }
      return new File([u8arr], filename, { type: mime });
    },
  },
};
</script>

<style lang="scss">
.id-modal {
  background-color: #f6f6f6;
  // height: 50vh;

  // @include media-breakpoint-down(xs) {
  //   height: 30vh;
  // }

  img {
    height: 50%;
  }
}

.id-body-modal {
  overflow: auto;
  overflow-x: hidden;
}
</style>

<style lang="scss" scoped>
.separator {
  margin-bottom: 10px;
}
.modal-cancel-btn.btn-outline-info {
  border-color: #cdfcb1;
  background-color: transparent;
  color: #cdfcb1;

  &:active {
    background-color: transparent;
    border-color: #cdfcb1;
    box-shadow: 0 0 0 0.15rem rgba(#cdfcb1, 0.5);
  }

  &:focus {
    box-shadow: 0 0 0 0.15rem rgba(#cdfcb1, 0.5);
  }
}

.photo-holder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 250px;
  margin-bottom: 20px;
  background-color: #f2f2f2;
  position: relative;

  .btn-crop {
    padding: 0.75rem;
    background-color: white;
    cursor: pointer;
    position: absolute;
    top: 10px;
    right: 10px;
    color: #a783ff;
  }

  @include media-breakpoint-up(xxl) {
    height: 250px * $scale-font;
    margin-bottom: 20px * $scale-font;
  }

  .photo {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 300px;
    height: 200px;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;

    @include media-breakpoint-up(xxl) {
      width: 300px * $scale-font;
      height: 200px * $scale-font;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
}
.photo-taken {
  position: absolute;
  left: -9999px;
}

.sub-text {
  a {
    cursor: pointer;
  }
}

.list-id {
  margin-bottom: -15px;
}
</style>
